import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircle from '@material-ui/icons/CheckCircle'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { Loading } from 'react-admin'
import {
  error,
  errorD1,
  kellyGreen,
  lightGray1,
  lightgreye3,
  marigoldOrange,
  midGray,
  paleyellow,
  success,
} from 'shared/theme/colors'

import { BaseActionDetailButton } from 'components/Common/ActionButtons'
import { OnelineAddress } from 'components/Common/Loads'
import { JobStatus } from 'helpers/constants'
import { getLocalizedStopAppointmentWindow } from 'helpers/loads'
import { captureExceptionIfProduction } from 'helpers/utils'
import {
  Job,
  LoadStop,
  TruckercloudIntegrationStatusEnum,
  TruckercloudStopStatus,
  TruckercloudStopStatusName,
} from 'models/jobs'
import { getTruckercloudRouteProgress, reprocessTruckercloud } from '../../api/jobs'
import { getProductNameFromRecord } from '../../helpers/utils'
import { BoxedGridItem } from '../Common/BoxedGridItem'

function getLoadStopProgressStatusBackgroundColor(tracking_status: String) {
  switch (tracking_status) {
    case TruckercloudStopStatusName.QUEUED:
      return lightgreye3
    case TruckercloudStopStatusName.IN_PROGRESS:
      return paleyellow
    case TruckercloudStopStatusName.CONFIRMED:
      return success
    case TruckercloudStopStatusName.NOT_CONFIRMED:
      return error
    case TruckercloudStopStatusName.ERROR:
      return error
    default:
      return success
  }
}

function getLoadStopProgressStatusTextColor(tracking_status: String) {
  switch (tracking_status) {
    case TruckercloudStopStatusName.QUEUED:
      return midGray
    case TruckercloudStopStatusName.IN_PROGRESS:
      return marigoldOrange
    case TruckercloudStopStatusName.CONFIRMED:
      return kellyGreen
    case TruckercloudStopStatusName.NOT_CONFIRMED:
      return errorD1
    case TruckercloudStopStatusName.ERROR:
      return errorD1
    default:
      return kellyGreen
  }
}

function shouldDisplayDeliveryConfirmation(trackingStatus: TruckercloudStopStatus) {
  // We only want to display the delivery confirmation to the user if it is not queued
  return trackingStatus !== TruckercloudStopStatus.QUEUED
}

interface LoadStopProgressSectionProps {
  record: Job
  loadStopProgress: any
}

interface StylesProps {
  statusColor: string
  statusBackgroundColor: string
}

interface StopStatusErrorMessage {
  stopStatus: TruckercloudStopStatus
  errorMessage: string
}

interface StopStatusProps {
  [stop_id: string]: StopStatusErrorMessage
}

const useStyles = makeStyles({
  trackingStatus: (props: StylesProps) => ({
    backgroundColor: props.statusBackgroundColor,
    color: props.statusColor,
    padding: '5px',
  }),
  addressLine: {
    color: midGray,
  },
  etaIcon: (props: StylesProps) => ({
    color: props.statusColor,
  }),
  heading: {
    color: lightGray1,
    marginRight: '10px',
  },
})

const LoadStopSection = (props: any) => {
  const { stop, stopStatus, errorMessage } = props

  // @ts-ignore
  const statusToDisplay = TruckercloudStopStatusName[stopStatus]

  const stylesProps = {
    statusColor: getLoadStopProgressStatusTextColor(statusToDisplay),
    statusBackgroundColor: getLoadStopProgressStatusBackgroundColor(statusToDisplay),
  }
  const classes = useStyles(stylesProps)
  const hasArrivedAtStop = TruckercloudStopStatus.CONFIRMED === stopStatus

  const shouldDisplayDeliveryConfirmationforStop = shouldDisplayDeliveryConfirmation(stopStatus)
  return shouldDisplayDeliveryConfirmationforStop ? (
    <Grid container item xs={10} direction="column" spacing={2}>
      <Grid container item direction="row">
        <Grid item xs={2}>
          <Box
            component="div"
            sx={{ display: 'inline-flex', alignItems: 'center', gridGap: 3 }}
            className={classes.trackingStatus}
          >
            {hasArrivedAtStop && <CheckCircle fontSize="small" />}
            <Typography variant="body2" component="span">
              {statusToDisplay}
            </Typography>
          </Box>
        </Grid>
        <Grid container item direction="column" xs={5} spacing={1}>
          <Grid item>
            <Typography variant="body2">{`STOP ${stop.sequence_number} ${_.startCase(
              stop.type_detail.toLowerCase()
            )} ${_.startCase(stop.loading_type?.toLowerCase())}`}</Typography>
          </Grid>
          {stop.location_name && (
            <Grid item>
              <Typography variant="body2">{stop.location_name}</Typography>
            </Grid>
          )}
          <Grid item>
            <OnelineAddress record={stop} label={classes.addressLine} />
          </Grid>
        </Grid>
        <Grid item container direction="column" xs={5} spacing={1}>
          <BoxedGridItem
            heading="Appointment"
            value={getLocalizedStopAppointmentWindow(stop)}
            classes={classes}
          />
        </Grid>
      </Grid>
      {errorMessage && (
        <Grid item>
          <Typography variant="body2" color="error">
            {errorMessage}
          </Typography>
        </Grid>
      )}
    </Grid>
  ) : null
}

const ReprocessButon = ({ record }: any) => (
  <BaseActionDetailButton
    record={record}
    actionRequest={reprocessTruckercloud}
    actionTitle="Reprocess"
    actionText="Reprocess TruckerCloud tracking for this load. This may take up to a minute."
  />
)

const LoadStopProgressSection = ({ record, loadStopProgress }: LoadStopProgressSectionProps) => {
  const [mapStopStatus, setMapStopStatus] = useState({})
  const stops: LoadStop[] = record.load.stops
  useEffect(() => {
    let updatedMapStopStatus = {}
    for (const loadStopPair of loadStopProgress) {
      updatedMapStopStatus = {
        ...updatedMapStopStatus,
        [loadStopPair.stop_id]: {
          stopStatus: loadStopPair.stop_status,
          errorMessage: loadStopPair.error_message || '',
        },
      }
    }
    setMapStopStatus(updatedMapStopStatus as StopStatusProps)
  }, [loadStopProgress])
  const trackingAvailable = [JobStatus.Current, JobStatus.Past].includes(record.status)
  // We only want to display delivery confirmations in a 'terminal' state (Confirmed, Not Confirmed, or Error)
  const anyDeliveryConfirmationsInTerminalState =
    loadStopProgress.filter((stopProgress: any) =>
      shouldDisplayDeliveryConfirmation(stopProgress.stop_status)
    ).length > 0
  return (
    <Grid container item direction="column" spacing={2}>
      {trackingAvailable && anyDeliveryConfirmationsInTerminalState && <ReprocessButon record={record} />}
      <Grid container item direction="row" justifyContent="flex-start" spacing={0}>
        {trackingAvailable &&
          anyDeliveryConfirmationsInTerminalState &&
          stops &&
          stops.map((stop: LoadStop) => {
            const stopStatusError = (mapStopStatus as StopStatusProps)[stop.id]
            return (
              stopStatusError && (
                <LoadStopSection
                  stop={stop}
                  stopStatus={stopStatusError.stopStatus}
                  errorMessage={stopStatusError.errorMessage}
                />
              )
            )
          })}
        {!anyDeliveryConfirmationsInTerminalState && (
          <Grid container item direction="column" spacing={2}>
            <Grid item>
              <Typography variant="body2">Delivery Confirmations in Progress</Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

const TruckercloudRouteProgressSection = (props: any) => {
  const { record } = props
  const [routeProgress, setRouteProgress] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<any>(null)
  const [invalidJobForRoute, setInvalidJobForRoute] = useState(false)
  const [notApplicableRouteProgress, setNotApplicableRouteProgress] = useState(false)
  const [noDataReceivedForJob, setNoDataReceivedForJob] = useState(false)
  const [productName, setProductName] = useState('NO PRODUCT SUBSCRIPTION')
  const showLoadStopSection = error === null && !noDataReceivedForJob && !loading

  useEffect(() => {
    async function loadTruckercloudProgress() {
      if (![JobStatus.Current, JobStatus.Past].includes(record.status)) {
        setInvalidJobForRoute(true)
        setLoading(false)
      } else if (record.team.is_premium === true) {
        setNotApplicableRouteProgress(true)
        setLoading(false)
      } else if (record?.truckercloud_verification_status === TruckercloudIntegrationStatusEnum.NO_DATA) {
        setNoDataReceivedForJob(true)
        setLoading(false)
      } else {
        setLoading(true)
        try {
          const response = await getTruckercloudRouteProgress(record.id)
          setLoading(false)
          setRouteProgress(response)
        } catch (err: any) {
          setLoading(false)
          setError(err.response.data.error)
          captureExceptionIfProduction(err)
        }
      }
    }
    setProductName(getProductNameFromRecord(record, true))
    if (productName !== 'NO PRODUCT SUBSCRIPTION') {
      loadTruckercloudProgress()
    }
  }, [record, productName, setProductName, setLoading, setRouteProgress, setError])
  return (
    <Grid container direction="column" spacing={4}>
      <Grid item>
        <Typography variant="h5">ELD Route Progress</Typography>
      </Grid>
      <Grid item>
        <Typography variant="h6">Truckercloud</Typography>
      </Grid>
      <Grid container item direction="column">
        {invalidJobForRoute && (
          <Typography variant="body1">Route progress available after job starts.</Typography>
        )}
        {notApplicableRouteProgress && (
          <Typography variant="body1">
            No Truckercloud Route Progress for this team's product subscription.
          </Typography>
        )}
        {noDataReceivedForJob && (
          <Typography variant="body1">No Truckercloud Delivery Confirmation received for team.</Typography>
        )}
        {loading && <Loading />}
        {error && <Typography variant="body1">{error}</Typography>}
        {showLoadStopSection && <LoadStopProgressSection record={record} loadStopProgress={routeProgress} />}
      </Grid>
    </Grid>
  )
}

export default TruckercloudRouteProgressSection
